import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  Globe,
  MapPin,
  ChevronRight
} from 'lucide-react';
import OrganizationDetailView from './OrganizationDetailView';
import { categoriesAPI } from '@/lib/api';
import { ClientStatusPills, ClientAVBackupsFields } from '@/components/ClientStatusBadges';

import { BACKEND_URL } from '@/lib/backend';

const EMPTY_ORG_FORM = {
  name: '',
  description: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  primary_contact: '',
  notes: '',
  antivirus: '',
  backups: [],
};

export default function OrganizationsModule() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [antivirusOptions, setAntivirusOptions] = useState([]);
  const [backupTypeOptions, setBackupTypeOptions] = useState([]);
  const [formData, setFormData] = useState({ ...EMPTY_ORG_FORM });

  useEffect(() => {
    loadOrganizations();
    loadCategoryOptions();
  }, []);

  const loadCategoryOptions = async () => {
    try {
      const [av, bk] = await Promise.all([
        categoriesAPI.list({ type: 'antivirus' }),
        categoriesAPI.list({ type: 'backup' }),
      ]);
      setAntivirusOptions((av.data || []).map((c) => c.name));
      setBackupTypeOptions((bk.data || []).map((c) => c.name));
    } catch (err) {
      console.error('Failed to load antivirus/backup categories', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/documentation/organizations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        backups: (formData.backups || []).filter((b) => (b.type || '').trim()),
      };
      const response = await fetch(`${BACKEND_URL}/api/documentation/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setShowAddDialog(false);
        setFormData({ ...EMPTY_ORG_FORM });
        loadOrganizations();
      } else {
        alert('Failed to create organization');
      }
    } catch (err) {
      console.error('Failed to create organization:', err);
      alert('Failed to create organization');
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name || '',
      description: org.description || '',
      address: org.address || '',
      phone: org.phone || '',
      email: org.email || '',
      website: org.website || '',
      primary_contact: org.primary_contact || '',
      notes: org.notes || '',
      antivirus: org.antivirus || '',
      backups: Array.isArray(org.backups)
        ? org.backups.map((b) => ({
            type: b.type || '',
            status: b.status || 'successful',
            last_updated_at: b.last_updated_at,
          }))
        : [],
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        backups: (formData.backups || []).filter((b) => (b.type || '').trim()),
      };
      const response = await fetch(`${BACKEND_URL}/api/documentation/organizations/${editingOrg.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setShowEditDialog(false);
        setEditingOrg(null);
        setFormData({ ...EMPTY_ORG_FORM });
        loadOrganizations();
      } else {
        alert('Failed to update organization');
      }
    } catch (err) {
      console.error('Failed to update organization:', err);
      alert('Failed to update organization');
    }
  };

  const handleDelete = async (orgId, orgName) => {
    if (!confirm(`Are you sure you want to delete ${orgName}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/organizations/${orgId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        loadOrganizations();
      }
    } catch (err) {
      console.error('Failed to delete organization:', err);
      alert('Failed to delete organization');
    }
  };

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    selectedOrg ? (
      <OrganizationDetailView organization={selectedOrg} onBack={() => setSelectedOrg(null)} />
    ) : (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Organizations</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage client organizations and their documentation
            </p>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full h-11 self-start sm:self-auto">
                <Plus className="w-4 h-4 mr-2" />
                Add Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Organization</DialogTitle>
                <DialogDescription>
                  Create a new organization to manage documentation
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Organization Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Acme Corporation"
                      required
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Brief description of the organization"
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="contact@acme.com"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="123 Main St, City, State ZIP"
                    />
                  </div>
                  
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      placeholder="https://acme.com"
                    />
                  </div>
                  
                  <div>
                    <Label>Primary Contact</Label>
                    <Input
                      value={formData.primary_contact}
                      onChange={(e) => setFormData({...formData, primary_contact: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Additional notes..."
                      rows={3}
                    />
                  </div>
                </div>

                <ClientAVBackupsFields
                  data={formData}
                  onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
                  antivirusOptions={antivirusOptions}
                  backupTypeOptions={backupTypeOptions}
                  testIdPrefix="create-org"
                />
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                    Create Organization
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Organization Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>
              Update organization information
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Organization Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Acme Corporation"
                  required
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the organization"
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="contact@acme.com"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Main St, City, State, ZIP"
                />
              </div>
              
              <div>
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://acme.com"
                />
              </div>
              
              <div>
                <Label>Primary Contact</Label>
                <Input
                  value={formData.primary_contact}
                  onChange={(e) => setFormData({...formData, primary_contact: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
            </div>

            <ClientAVBackupsFields
              data={formData}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
              antivirusOptions={antivirusOptions}
              backupTypeOptions={backupTypeOptions}
              testIdPrefix="edit-org"
            />
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                setShowEditDialog(false);
                setEditingOrg(null);
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                Update Organization
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Organizations Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-slate-500">Loading organizations...</p>
        </div>
      ) : filteredOrgs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-3" />
            <p className="text-slate-600 font-medium mb-1">No organizations found</p>
            <p className="text-sm text-slate-500">
              {searchQuery ? 'Try a different search term' : 'Create your first organization to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOrgs.map((org) => (
            <Card
              key={org.id}
              className="hover:shadow-lg hover:border-cyan-300 transition-all cursor-pointer group"
              onClick={() => setSelectedOrg(org)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedOrg(org); } }}
              data-testid={`org-tile-${org.id}`}
              aria-label={`Open ${org.name} details`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-5 h-5 text-blue-600 shrink-0" />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSelectedOrg(org); }}
                        className="text-left font-semibold text-lg text-slate-800 hover:text-cyan-600 transition-colors truncate"
                        data-testid={`org-name-${org.id}`}
                      >
                        {org.name}
                      </button>
                    </div>
                    <ClientStatusPills entity={org} entityId={org.id} />
                    {org.description && (
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">{org.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(org)} aria-label="Edit organization">
                      <Edit className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(org.id, org.name)} aria-label="Delete organization">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-2">
                {org.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600 truncate">{org.phone}</span>
                  </div>
                )}
                {org.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600 truncate">{org.email}</span>
                  </div>
                )}
                {org.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <a 
                      href={org.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-cyan-600 hover:underline truncate"
                    >
                      {org.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                {org.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <span className="text-slate-600 line-clamp-2">{org.address}</span>
                  </div>
                )}
                <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-end text-xs text-cyan-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  View all documentation <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
    )
  );
}
